import axios from "axios";
import { AsyncPoller } from "./http/asyncPoller";
import { getItemAnon } from "./localstorage";
import batchAdapter from "./http/batchAdapter";
import { getStoreState } from "./storeRegistry";

export const externalClient = axios.create();
export const resolverManager = {};
const timeoutManager = {}; // Track timeouts for cleanup

export const occmClient = axios.create({
    baseURL: process.env.REACT_APP_OCCM_ENDPOINT,
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    },
    adapter: process.env.REACT_APP_IS_SAAS === "true" ? batchAdapter : undefined
});

export const forwarderClient = axios.create({
    baseURL: process.env.REACT_APP_FORWARDER_BACKEND,
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    },
    adapter: process.env.REACT_APP_IS_SAAS === "true" ? batchAdapter : undefined
});

const isNewAsync = getItemAnon({itemKey: `feature.enable-async`});
const isNewBatching = getItemAnon({itemKey: `feature.enable-batching`});
const isDisableAsync = isNewAsync || isNewBatching || getItemAnon({itemKey: `feature.disable-async`});


let asyncPoller = null;
if(!isDisableAsync) {
    asyncPoller = new AsyncPoller(occmClient);
}

let isMessageHandlerInitialized = false;

export const pauseAsyncPoller = () => {
    if(asyncPoller) {
        asyncPoller.pause();
    }
}

// Add cleanup mechanism for background revalidation promises
const cleanupResolver = (key, timeout = 120000) => {
    // Cancel any existing timeout for this key to prevent duplicate timeouts
    if (timeoutManager[key]) {
        clearTimeout(timeoutManager[key]);
    }
    
    // Set a new timeout - if we don't hear back in 30s, resolve with null
    timeoutManager[key] = setTimeout(() => {
        const resolver = resolverManager[key];
        if (resolver) {
            // Timeout hit - likely means data unchanged or slow revalidation
            // Resolve with null (not an error - stale data was already returned)
            resolver.resolve(new Error('Service worker revalidation timeout'));
            delete resolverManager[key];
        }
        delete timeoutManager[key];
    }, timeout);
};

export const apiClient = axios.create({
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    },
    adapter: process.env.REACT_APP_IS_SAAS === "true" ? batchAdapter : undefined
});

// In axios 1.x, FormData doesn't auto-override the default Content-Type.
// This interceptor ensures multipart/form-data is set when body contains files,
// by checking for File/Blob objects within FormData or as direct payload.
const formDataInterceptor = (config) => {
    let hasFile = false;
    // 1. Check if the data is a FormData object
    if (config.data instanceof FormData) {
        // Look through all items inside the FormData
        for (const value of config.data.values()) {
            if (value instanceof File || value instanceof Blob) {
                hasFile = true;
                break;
            }
        }
    }
    // 2. Check if the data itself is a direct File or Blob
    else if (config.data instanceof File || config.data instanceof Blob) {
        hasFile = true;
    }
    // 3. Take action based on the result
    if (hasFile) {
        config.headers['Content-Type'] = 'multipart/form-data';
    }
    return config;
};

const workspaceIdInterceptor = (config) => {
    try {
        const workspaceId = getStoreState()?.tenancy?.selectedScope?.id;
        if (workspaceId) {
            config.headers['x-workspace-id'] = workspaceId;
        }
    } catch {
        
    }
    return config;
};

apiClient.interceptors.request.use(formDataInterceptor);
occmClient.interceptors.request.use(workspaceIdInterceptor);
occmClient.interceptors.request.use(formDataInterceptor);
forwarderClient.interceptors.request.use(formDataInterceptor);
export const downloadCSVClient = axios.create({
    headers: {
        "Content-Type": "text/csv",
        "Accept": "text/csv"
    }
});

export const setExtentedUsersHeaders = (extededHeader) => {
    apiClient.defaults.headers['Accept'] = extededHeader;
};

export const getOccmConfigForAgent = (agentId) => {
    const config = occmClient.defaults
    return { baseURL: config.baseURL, headers: {...config.headers, "X-Agent-Id": agentId}, adapter: config.adapter}
};

export const setOccmAgent = ({agentId, accountId}) => {
    occmClient.defaults.headers['X-Agent-Id'] = agentId;
    occmClient.defaults.headers['X-Tenancy-Account-Id'] = accountId;
};

export const setForwarderAgent = ({agentId, accountId}) => {
    forwarderClient.defaults.headers['X-Agent-Id'] = agentId;
    forwarderClient.defaults.headers['X-Tenancy-Account-Id'] = accountId;
}

export const setAccessToken = (accessToken) => {
    apiClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    occmClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    forwarderClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    downloadCSVClient.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
};

/**
 * Generates a custom cache control header object for HTTP requests.
 *
 * @param {Object} options - The options for cache control.
 * @param {number} [options.maxAge] - The maximum age (in seconds) for the cache.
 * @param {number} [options.maxStale] - The maximum staleness (in seconds) allowed for the cache.
 * @param {string} [options.strategy] - The caching strategy to use.
 * @param {string} [options.serviceKey] - A unique key identifying the service.
 * @returns {Object} An object containing the "X-Cache-Control" header if caching is enabled and parameters are provided, otherwise an empty object.
 */
export const getCacheControlHeader = ({maxAge, maxStale, strategy, serviceKey}) => {
    const headerParams = [];
    if (maxAge !== undefined) {
        headerParams.push(`max-age=${maxAge}`);
    }
    if (maxStale !== undefined) {
        headerParams.push(`max-stale=${maxStale}`);
    }
    if (strategy) {
        headerParams.push(`strategy=${strategy}`);
    }
    if (serviceKey) {
        headerParams.push(`key=${serviceKey}`);
    }
    return headerParams.length > 0 ? {"X-Cache-Control": headerParams.join(', ')} : {};
}

/**
 * Initializes the Service Worker message handler.
 * 
 * Sets up an event listener for messages from the Service Worker, handling
 * responses based on the message type and updating the resolverManager accordingly.
 * Only activates if browser cache is enabled and Service Worker is supported.
 * 
 * @function initialSWMessageHandler
 * @returns {void}
 */
export const initialSWMessageHandler = () => {    
    // Prevent duplicate listeners
    if (isMessageHandlerInitialized) {
        console.log("[Client] SW message handler already initialized");
        return;
    }
    
    const convertToAxiosSuccessResponse = (response) => {
        return {
            data: response,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: {},
            request: {}
        };
    }

    const convertToAxiosErrorResponse = (error) => {
        return {
            response: {
                data: error,
                status: error.status || 500,
                statusText: error.statusText || 'Error',
                headers: {},
                config: {},
                request: {}
            }
        };
    }
    
    /**
     * Handles message events by resolving or rejecting promises based on the event data.
     * It checks if the event type exists in the resolverManager and processes the payload accordingly.
     * Removes the resolver from resolverManager after handling.
     *
     * @async
     * @param {MessageEvent} event - The message event containing data from the service worker.
     * @returns {Promise<void>}
     */
    const handleMessageEvent = async (event) => {
        try {
            if (event.data && resolverManager[event.data.type]) {
                if (event.data.payload !== null && event.data.payload !== undefined) {
                    const resolver = resolverManager[event.data.type];
                    if (event.data.isError) {
                        resolver?.reject(convertToAxiosErrorResponse({ ...event.data.payload, sw_api_call: true, hasChanges: true }));
                    } else {
                        resolver?.resolve(convertToAxiosSuccessResponse({ ...event.data.payload, sw_api_call: true, hasChanges: true }));
                    }
                } else {
                    resolverManager[event.data.type]?.resolve({ hasChanges: false, sw_api_call: true });
                }
                
                delete resolverManager[event.data.type];
                
                // Clean up the timeout since we got a response
                if (timeoutManager[event.data.type]) {
                    clearTimeout(timeoutManager[event.data.type]);
                    delete timeoutManager[event.data.type];
                }
            }
        } catch (error) {
            console.error('SW message handler error:', error);
        }
    };
    
    try {
        navigator.serviceWorker?.addEventListener('message', handleMessageEvent);
        isMessageHandlerInitialized = true;
    } catch (error) {
        console.error('Failed to setup SW message listener:', error);
    }
};

/**
 * Retrieves a cached client response or makes a network request based on the provided cache strategy.
 *
 * @param {Object} client - The HTTP client instance (e.g., Axios).
 * @param {string} url - The endpoint URL for the request.
 * @param {string} serviceKey - Unique key to identify the service for caching.
 * @param {Object} [additionalOptions] - Additional options for the request.
 * @param {Object} [additionalOptions.config] - Extra configuration for the client request.
 * @param {boolean} [additionalOptions.isGQL=false] - Whether the request is a GraphQL POST.
 * @param {Object} [additionalOptions.body] - Request body for GraphQL POST.
 * @param {Object} [additionalOptions.query] - Query parameters for the request.
 * @param {Object} [additionalOptions.headers] - Custom headers for the request.
 * @param {number} [additionalOptions.maxAge] - Maximum age for cache control.
 * @param {number} [additionalOptions.maxStale] - Maximum staleness for cache control.
 * @param {boolean} [ignoreCache=false] - If true, bypasses cache and fetches from network.
 * @param {string} [cacheStrategy="stale_while_revalidate"] - Cache strategy to use ("network_first", "stale_while_revalidate", etc.).
 * @returns {Promise<Object>} Promise resolving to the client response or rejecting with an error.
 */
export const getCachedClient = (client, url, serviceKey, additionalOptions = undefined, ignoreCache=false, cacheStrategy="stale_while_revalidate") => {
    const {config, isGQL=false, body=undefined, query, headers, maxAge, maxStale} = additionalOptions || {};
    const strategy = ignoreCache ? "network_first" : cacheStrategy;

    const key = serviceKey.toLowerCase();

    let cachedHeaders = getCacheControlHeader({maxAge, maxStale, strategy, serviceKey: key});
    if (headers) {
        cachedHeaders = { ...cachedHeaders, ...headers };
    }

    const apiCall = isGQL ? client.post(url, body, {headers: cachedHeaders, ...config, params: query}) : client.get(url, {headers: cachedHeaders, ...config, params: query})
    let networkResponse = undefined;
    if (strategy === 'stale_while_revalidate') {
        // Create a NEW promise for the fresh data
        networkResponse = new Promise((resolveFresh, rejectFresh) => {
            resolverManager[key] = {resolve: resolveFresh, reject: rejectFresh};
            cleanupResolver(key); // Set timeout (will cancel any existing one)
        });
    }
    return new Promise((resolve, reject) => {
        apiCall.then(response => {
            // Store resolver for fresh data updates from service worker
            // Only for stale_while_revalidate strategy
            resolve({response, networkResponse});
        }).catch(error => {
            cleanupResolver(key); // Add cleanup
            reject({error, networkResponse});
        });
    });
};

export const hasNetworkResponse = (response) => {
    return response && response.data && response.data.hasChanges === true ? true : false;
}

export const handleNetworkResponse = (networkResponse, handleResponse, handleError, args) => {
    if (networkResponse !== undefined) {
        networkResponse.then((serverResponse) => {
            if (hasNetworkResponse(serverResponse)) {
                handleResponse(serverResponse, args);
            }
        }).catch((e) => {
            handleError(e, args)
        });
    }
}

export const recursiveGet = async (url, config) => {
    let hasMoreData = true;
    const returnValue = [];
    let count = 0;

    while (hasMoreData) {
        const { data } = await apiClient.get(url, config);
        hasMoreData = !!data.next;
        count = data.count
        if(data.next) {
            config = config || {};
            config.params = config.params || {};
            config.params.nextToken = data.next;
        }
        returnValue.push(...data.items)
    }

    return {
        data: {
            count,
            items: returnValue,
            next: null
        }
    };
};
